// Convert measurements between metric and imperial
export const convertMeasurement = (amount, unit, toMetric) => {
    if (toMetric) {
        // Imperial to Metric conversions
        const conversions = {
            cup: { multiplier: 240, unit: "ml" },
            tbsp: { multiplier: 15, unit: "ml" },
            tsp: { multiplier: 5, unit: "ml" },
            oz: { multiplier: 28.35, unit: "g" },
            lb: { multiplier: 453.59, unit: "g" },
            lbs: { multiplier: 453.59, unit: "g" },
            "fl oz": { multiplier: 29.57, unit: "ml" },
        };
        const conversion = conversions[unit.toLowerCase()];
        if (conversion) {
            return {
                amount: Math.round(amount * conversion.multiplier * 10) / 10,
                unit: conversion.unit,
            };
        }
    }
    else {
        // Metric to Imperial conversions
        const conversions = {
            ml: { multiplier: 0.00422675, unit: "cup" },
            g: { multiplier: 0.00220462, unit: "lb" },
            kg: { multiplier: 2.20462, unit: "lb" },
        };
        const conversion = conversions[unit.toLowerCase()];
        if (conversion) {
            const converted = amount * conversion.multiplier;
            // Smart unit selection
            if (unit.toLowerCase() === "ml") {
                if (converted < 0.0625) {
                    return { amount: Math.round(converted * 48 * 10) / 10, unit: "tsp" };
                }
                else if (converted < 0.25) {
                    return { amount: Math.round(converted * 16 * 10) / 10, unit: "tbsp" };
                }
                return { amount: Math.round(converted * 10) / 10, unit: "cup" };
            }
            if (unit.toLowerCase() === "g" || unit.toLowerCase() === "kg") {
                if (converted < 0.0625) {
                    return { amount: Math.round(converted * 16 * 10) / 10, unit: "oz" };
                }
                return { amount: Math.round(converted * 10) / 10, unit: "lb" };
            }
            return { amount: Math.round(converted * 10) / 10, unit: conversion.unit };
        }
    }
    // No conversion available, return original
    return { amount, unit };
};
// Scale ingredient amounts based on servings
export const scaleIngredients = (ingredients, originalServings, newServings) => {
    const scaleFactor = newServings / originalServings;
    return ingredients.map((ingredient) => (Object.assign(Object.assign({}, ingredient), { amount: ingredient.amount ? Math.round(ingredient.amount * scaleFactor * 100) / 100 : undefined, metric_g: ingredient.metric_g
            ? Math.round(ingredient.metric_g * scaleFactor * 100) / 100
            : undefined, metric_ml: ingredient.metric_ml
            ? Math.round(ingredient.metric_ml * scaleFactor * 100) / 100
            : undefined, weight_g: ingredient.weight_g
            ? Math.round(ingredient.weight_g * scaleFactor * 100) / 100
            : undefined })));
};
// Scale cooking time based on servings (rough estimate)
export const scaleTime = (originalTime, originalServings, newServings) => {
    // Time doesn't scale linearly, use a logarithmic approximation
    const scaleFactor = newServings / originalServings;
    if (scaleFactor === 1)
        return originalTime;
    // If doubling, add 25% time; if halving, reduce by 15%
    const timeAdjustment = scaleFactor > 1
        ? 1 + (scaleFactor - 1) * 0.25
        : 1 - (1 - scaleFactor) * 0.15;
    return Math.round(originalTime * timeAdjustment);
};
// Format time display
export const formatTime = (minutes) => {
    if (minutes < 60) {
        return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) {
        return `${hours} hr${hours > 1 ? "s" : ""}`;
    }
    return `${hours} hr${hours > 1 ? "s" : ""} ${mins} min`;
};
// Format ingredient for display - handles DetailedRecipeIngredient
export const formatIngredient = (ingredient, useMetric) => {
    // Extract ingredient name - handle both 'item' and 'ingredient' fields
    const ingredientName = ingredient.item || ingredient.ingredient || 'Unknown ingredient';
    // Extract amount and unit - handle metric conversion
    let amount;
    let unit;
    if (useMetric) {
        // For DetailedRecipeIngredient, use metric_g or metric_ml
        if (ingredient.metric_g !== undefined && ingredient.metric_g !== null) {
            amount = ingredient.metric_g;
            unit = 'g';
        }
        else if (ingredient.metric_ml !== undefined && ingredient.metric_ml !== null) {
            amount = ingredient.metric_ml;
            unit = 'ml';
        }
        else if (ingredient.weight_g !== undefined && ingredient.weight_g !== null) {
            amount = ingredient.weight_g;
            unit = 'g';
        }
        else {
            // Fallback to imperial amount
            amount = ingredient.amount || 0;
            unit = ingredient.unit || '';
        }
    }
    else {
        // Imperial
        amount = ingredient.amount || 0;
        unit = ingredient.unit || '';
    }
    // Extract notes - handle both 'notes' and 'note' fields
    const notes = ingredient.note || undefined;
    // Format fractional amounts nicely
    const fractionMap = {
        "0.25": "¼",
        "0.33": "⅓",
        "0.5": "½",
        "0.66": "⅔",
        "0.75": "¾",
    };
    let displayAmount = amount.toString();
    const decimalPart = (amount % 1).toFixed(2);
    if (fractionMap[decimalPart]) {
        const wholePart = Math.floor(amount);
        displayAmount = wholePart > 0
            ? `${wholePart} ${fractionMap[decimalPart]}`
            : fractionMap[decimalPart];
    }
    const notesText = notes ? ` (${notes})` : "";
    return `${displayAmount} ${unit} ${ingredientName}${notesText}`;
};
